import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler } from '../components/common/ErrorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Plus, Edit, Trash2, PiggyBank, Users, TrendingUp, TrendingDown, Download, FileSpreadsheet, FileText, Settings } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useToast from '../hooks/useToast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
  green: '#10b981',
  red: '#ef4444',
  blue: '#3b82f6',
  amber: '#f59e0b'
};

// Professional card styles with transparency
const cardStyle = {
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
};

const PettyCashDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchDashboard(); }, [date]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/petty-cash/dashboard', { params: { date } });
      setDashboard(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading dashboard..." />;

  const chartData = [
    { name: 'Expenses', value: dashboard?.expenses?.total || 0 },
    { name: 'Salary', value: dashboard?.salaries?.total || 0 }
  ];

  const attendanceData = [
    { name: 'Present', value: dashboard?.salaries?.present || 0, color: COLORS.green },
    { name: 'Absent', value: dashboard?.salaries?.absent || 0, color: COLORS.red }
  ];

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-3">
        <Input 
          type="date" 
          value={date} 
          onChange={(e) => setDate(e.target.value)} 
          className="w-40"
          data-testid="dashboard-date-input"
          style={{ ...cardStyle, padding: '0.5rem' }}
        />
       <Button
  onClick={() => navigate('/petty-cash/expenses')}
  data-testid="expense-entry-btn"
  className="
    bg-green-600 text-white
    hover:bg-green-700
    transition-all duration-200
    hover:shadow-md
    active:scale-95
  "
>
  <TrendingDown className="w-4 h-4 mr-2" />
  Expense Entry
</Button>

<Button
  onClick={() => navigate('/petty-cash/salaries')}
  variant="outline"
  data-testid="salary-entry-btn"
  className="
    border-blue-600 text-blue-700
    hover:bg-blue-50
    transition-all duration-200
    active:scale-95
  "
>
  <Users className="w-4 h-4 mr-2" />
  Salary Entry
</Button>

<Button
  onClick={() => navigate('/petty-cash/reports')}
  variant="outline"
  data-testid="reports-btn"
  className="
    border-gray-400 text-gray-700
    hover:bg-gray-100
    transition-all duration-200
    active:scale-95
  "
>
  <FileText className="w-4 h-4 mr-2" />
  Reports
</Button>

      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card style={cardStyle}>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-600 font-medium">Today's Expenses</p>
            <p className="text-2xl font-bold text-red-600 mt-2">{(dashboard?.expenses?.total || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-600 font-medium">Total Salary (Today)</p>
            <p className="text-2xl font-bold text-blue-600 mt-2">{(dashboard?.salaries?.total || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-600 font-medium">Present / Absent</p>
            <p className="text-2xl font-bold mt-2">
              <span className="text-green-600">{dashboard?.salaries?.present || 0}</span>
              {' / '}
              <span className="text-red-600">{dashboard?.salaries?.absent || 0}</span>
            </p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4">
            <p className="text-sm text-gray-600 font-medium">Paid / Unpaid</p>
            <p className="text-xl font-bold mt-2">
              <span className="text-green-600">{(dashboard?.expenses?.paid || 0).toLocaleString()}</span>
              {' / '}
              <span className="text-red-600">{(dashboard?.expenses?.unpaid || 0).toLocaleString()}</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card style={cardStyle}>
          <CardHeader>
            <CardTitle>Expense vs Salary</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `${value.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="value" fill={COLORS.blue} name="Amount ()" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card style={cardStyle}>
          <CardHeader>
            <CardTitle>Attendance Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={attendanceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {attendanceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Summary Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card style={cardStyle}>
          <CardHeader>
            <CardTitle className="text-base">Expense Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Total:</span>
              <span className="font-bold">{(dashboard?.expenses?.total || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Paid:</span>
              <span className="font-bold text-green-600">{(dashboard?.expenses?.paid || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Unpaid:</span>
              <span className="font-bold text-red-600">{(dashboard?.expenses?.unpaid || 0).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>

        <Card style={cardStyle}>
          <CardHeader>
            <CardTitle className="text-base">Attendance Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Strength:</span>
              <span className="font-bold">{(dashboard?.salaries?.present || 0) + (dashboard?.salaries?.absent || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Present:</span>
              <span className="font-bold text-green-600">{dashboard?.salaries?.present || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Absent:</span>
              <span className="font-bold text-red-600">{dashboard?.salaries?.absent || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ExpenseEntry = () => {
  const { showSuccess, showError } = useToast();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ rav: '', amount: '', remarks: '', status: 'Unpaid' });
  const [editId, setEditId] = useState(null);

  useEffect(() => { fetchExpenses(); }, [date]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/petty-cash/expenses', { params: { date } });
      setExpenses(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        date,
        amount: parseFloat(formData.amount)
      };
      
      if (editId) {
        await api.put(`/api/petty-cash/expenses/${editId}`, payload);
      } else {
        await api.post('/api/petty-cash/expenses', payload);
      }
      showSuccess('Expense saved successfully');
      setShowForm(false);
      setEditId(null);
      setFormData({ rav: '', amount: '', remarks: '', status: 'Unpaid' });
      fetchExpenses();
    } catch (err) {
      showError('Failed to save expense');
    }
  };

  const handleStatusToggle = async (expense) => {
    try {
      const newStatus = expense.status === 'Paid' ? 'Unpaid' : 'Paid';
      await api.put(`/api/petty-cash/expenses/${expense.id}`, { status: newStatus });
      fetchExpenses();
    } catch (err) {
      showError('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await api.delete(`/api/petty-cash/expenses/${id}`);
      showSuccess('Expense deleted');
      fetchExpenses();
    } catch (err) {
      showError('Failed to delete');
    }
  };

  const exportExpensesToExcel = () => {
    try {
      const expenseData = expenses.map((e, i) => ({
        'SL': i + 1,
        'DATE': e.date,
        'RAV': e.rav,
        'AMOUNT': e.amount,
        'REMARKS': e.remarks || '',
        'STATUS': e.status
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(expenseData);
      XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
      XLSX.writeFile(wb, `Expenses_${date}.xlsx`);
      showSuccess('Excel exported successfully');
    } catch (err) {
      showError('Failed to export Excel');
    }
  };

  const total = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const paid = expenses.filter(e => e.status === 'Paid').reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) return <LoadingSpinner text="Loading expenses..." />;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Label>Date:</Label>
          <Input 
            type="date" 
            value={date} 
            onChange={(e) => setDate(e.target.value)} 
            className="w-40"
            data-testid="expense-date-input"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={exportExpensesToExcel} variant="outline" data-testid="export-expenses-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={() => setShowForm(true)} data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Expense Table */}
      <Card style={cardStyle}>
        <CardHeader>
          <CardTitle>Today's Expenses ({new Date(date).toLocaleDateString()})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {expenses.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Rav (Description)</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((exp, i) => (
                    <TableRow key={exp.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell className="font-medium">{exp.rav}</TableCell>
                      <TableCell className="font-semibold">{exp.amount?.toLocaleString()}</TableCell>
                      <TableCell className="text-gray-600">{exp.remarks || '-'}</TableCell>
                      <TableCell>
                        <button 
                          onClick={() => handleStatusToggle(exp)}
                          data-testid={`status-toggle-${exp.id}`}
                        >
                          <Badge 
                            variant={exp.status === 'Paid' ? 'success' : 'destructive'}
                            className="cursor-pointer hover:opacity-80"
                          >
                            {exp.status}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => { 
                              setEditId(exp.id); 
                              setFormData({
                                rav: exp.rav,
                                amount: exp.amount,
                                remarks: exp.remarks || '',
                                status: exp.status
                              }); 
                              setShowForm(true); 
                            }}
                            data-testid={`edit-expense-${exp.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDelete(exp.id)}
                            data-testid={`delete-expense-${exp.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No expenses recorded for this date
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Footer */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Total Expenses</p>
            <p className="text-2xl font-bold mt-2">{total.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Paid Amount</p>
            <p className="text-2xl font-bold text-green-600 mt-2">{paid.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Unpaid Amount</p>
            <p className="text-2xl font-bold text-red-600 mt-2">{(total - paid).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Expense Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="expense-form-modal" style={cardStyle}>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit' : 'Add'} Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="rav">Rav (Description) *</Label>
              <Input 
                id="rav"
                value={formData.rav} 
                onChange={(e) => setFormData({ ...formData, rav: e.target.value })} 
                placeholder="Enter description"
                data-testid="expense-rav-input"
                required 
              />
            </div>
            <div>
              <Label htmlFor="amount">Amount () *</Label>
              <Input 
                id="amount"
                type="number" 
                step="0.01"
                value={formData.amount} 
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })} 
                data-testid="expense-amount-input"
                required 
              />
            </div>
            <div>
              <Label htmlFor="remarks">Remarks</Label>
              <Input 
                id="remarks"
                value={formData.remarks} 
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} 
                placeholder="Optional notes"
              />
            </div>
            <div>
              <Label>Status</Label>
              <RadioGroup 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
                className="flex gap-6 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Paid" id="paid" />
                  <Label htmlFor="paid" className="cursor-pointer">Paid</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Unpaid" id="unpaid" />
                  <Label htmlFor="unpaid" className="cursor-pointer">Unpaid</Label>
                </div>
              </RadioGroup>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowForm(false);
                setEditId(null);
                setFormData({ rav: '', amount: '', remarks: '', status: 'Unpaid' });
              }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-expense-btn">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SalaryEntry = () => {
  const { showSuccess, showError } = useToast();
  const [salaries, setSalaries] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [showForm, setShowForm] = useState(false);
  const [showRoleSettings, setShowRoleSettings] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [formData, setFormData] = useState({ name: '', role: '', amount: '', attendance: 'Pending' });
  const [editId, setEditId] = useState(null);

  useEffect(() => { fetchSalaries(); fetchRoles(); }, [date]);

  const fetchSalaries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/petty-cash/salaries', { params: { date } });
      setSalaries(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/api/petty-cash/salary-roles');
      setRoles(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...formData, 
        date, 
        amount: formData.attendance === 'Present' ? parseFloat(formData.amount) : null 
      };
      
      if (editId) {
        await api.put(`/api/petty-cash/salaries/${editId}`, payload);
      } else {
        await api.post('/api/petty-cash/salaries', payload);
      }
      showSuccess('Salary saved successfully');
      setShowForm(false);
      setEditId(null);
      setFormData({ name: '', role: '', amount: '', attendance: 'Pending' });
      fetchSalaries();
    } catch (err) {
      showError('Failed to save salary');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this salary entry?')) return;
    try {
      await api.delete(`/api/petty-cash/salaries/${id}`);
      showSuccess('Salary deleted');
      fetchSalaries();
    } catch (err) {
      showError('Failed to delete');
    }
  };

  const handleAddRole = async () => {
    if (!newRole.trim()) return;
    try {
      await api.post('/api/petty-cash/salary-roles', { name: newRole });
      showSuccess('Role added');
      setNewRole('');
      fetchRoles();
    } catch (err) {
      showError('Failed to add role');
    }
  };

  const handleDeleteRole = async (id) => {
    try {
      await api.delete(`/api/petty-cash/salary-roles/${id}`);
      fetchRoles();
    } catch (err) {
      showError('Failed to delete role');
    }
  };

  const present = salaries.filter(s => s.attendance === 'Present');
  const absent = salaries.filter(s => s.attendance === 'Absent');
  const totalSalary = present.reduce((sum, s) => sum + (s.amount || 0), 0);

  // Group by role
  const groupedByRole = salaries.reduce((acc, s) => {
    const role = s.role || 'Other';
    if (!acc[role]) acc[role] = [];
    acc[role].push(s);
    return acc;
  }, {});

  if (loading) return <LoadingSpinner text="Loading salaries..." />;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Label>Date:</Label>
          <Input 
            type="date" 
            value={date} 
            onChange={(e) => setDate(e.target.value)} 
            className="w-40"
            data-testid="salary-date-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
  onClick={() => setShowRoleSettings(true)}
  variant="outline"
  data-testid="salary-role-settings-btn"
  className="
    border-gray-400 text-gray-700
    hover:bg-gray-100
    transition-all duration-200
    active:scale-95
  "
>
  <Settings className="w-4 h-4 mr-2" />
  Role Settings
</Button>

<Button
  onClick={() => setShowForm(true)}
  data-testid="add-salary-btn"
  className="
    bg-blue-700 text-white
    hover:bg-blue-800
    transition-all duration-200
    hover:shadow-md
    active:scale-95
  "
>
  <Plus className="w-4 h-4 mr-2" />
  Add Salary Entry
</Button>

        </div>
      </div>

      {/* Salary Tables Grouped by Role */}
      <div className="space-y-4">
        {Object.entries(groupedByRole).map(([role, items]) => (
          <Card key={role} style={cardStyle}>
            <CardHeader className="py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
              <CardTitle className="text-base font-semibold text-gray-800">Role: {role.toUpperCase()}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Attendance</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((sal, i) => (
                      <TableRow key={sal.id}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell className="font-medium">{sal.name}</TableCell>
                        <TableCell className="font-semibold">
                          {sal.attendance === 'Absent' ? '—' : `${(sal.amount || 0).toLocaleString()}`}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              sal.attendance === 'Present' ? 'success' : 
                              sal.attendance === 'Absent' ? 'destructive' : 
                              'secondary'
                            }
                          >
                            {sal.attendance}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => { 
                                setEditId(sal.id); 
                                setFormData({
                                  name: sal.name,
                                  role: sal.role,
                                  amount: sal.amount || '',
                                  attendance: sal.attendance
                                }); 
                                setShowForm(true); 
                              }}
                              data-testid={`edit-salary-${sal.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleDelete(sal.id)}
                              data-testid={`delete-salary-${sal.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))}

        {salaries.length === 0 && (
          <Card style={cardStyle}>
            <CardContent className="text-center py-8 text-gray-500">
              No salary entries recorded for this date
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Total Present</p>
            <p className="text-2xl font-bold text-green-600 mt-2">{present.length}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Total Absent</p>
            <p className="text-2xl font-bold text-red-600 mt-2">{absent.length}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Strength</p>
            <p className="text-2xl font-bold mt-2">{salaries.length}</p>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-gray-600 font-medium">Total Salary</p>
            <p className="text-2xl font-bold text-blue-600 mt-2">{totalSalary.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Salary Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="salary-form-modal" style={cardStyle}>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit' : 'Add'} Salary Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input 
                id="name"
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                placeholder="Employee name"
                data-testid="salary-name-input"
                required 
              />
            </div>
            <div className="space-y-2">
  <Label htmlFor="role">Role *</Label>

  <Select
    value={formData.role}
    onValueChange={(v) => setFormData({ ...formData, role: v })}
  >
    <SelectTrigger className="w-full">
      <SelectValue placeholder="Select role" />
    </SelectTrigger>

    <SelectContent
      className="z-[9999] max-h-60 overflow-y-auto bg-white"
      position="popper"
      sideOffset={4}
    >
      {roles.map((r) => (
        <SelectItem key={r.id} value={r.name}>
          {r.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>

  <Input
    value={formData.role}
    onChange={(e) =>
      setFormData({ ...formData, role: e.target.value })
    }
    placeholder="Or type custom role"
    required
  />
</div>

            <div>
              <Label htmlFor="amount">Amount () *</Label>
              <Input 
                id="amount"
                type="number" 
                step="0.01"
                value={formData.amount} 
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })} 
                disabled={formData.attendance === 'Absent'}
                data-testid="salary-amount-input"
                required={formData.attendance === 'Present'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.attendance === 'Absent' && 'Amount disabled for absent attendance'}
              </p>
            </div>
            <div>
              <Label>Attendance</Label>
              <RadioGroup 
                value={formData.attendance} 
                onValueChange={(v) => setFormData({ ...formData, attendance: v, amount: v === 'Absent' ? '' : formData.amount })}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Pending" id="pending" />
                  <Label htmlFor="pending" className="cursor-pointer">Pending</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Present" id="present" />
                  <Label htmlFor="present" className="cursor-pointer text-green-600 font-medium">Present</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Absent" id="absent" />
                  <Label htmlFor="absent" className="cursor-pointer text-red-600 font-medium">Absent</Label>
                </div>
              </RadioGroup>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowForm(false);
                setEditId(null);
                setFormData({ name: '', role: '', amount: '', attendance: 'Pending' });
              }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-salary-btn">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Role Settings Modal */}
      <Dialog open={showRoleSettings} onOpenChange={setShowRoleSettings}>
        <DialogContent data-testid="salary-role-settings-modal" style={cardStyle}>
          <DialogHeader>
            <DialogTitle>Salary Roles</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input 
                placeholder="New role name"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddRole()}
                data-testid="new-salary-role-input"
              />
              <Button onClick={handleAddRole} data-testid="add-salary-role-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {roles.map(role => (
                <div key={role.id} className="flex justify-between items-center p-2 border rounded hover:bg-gray-50">
                  <span className="font-medium">{role.name}</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleDeleteRole(role.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {roles.length === 0 && (
                <p className="text-center text-gray-500 py-4">No roles added yet</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PettyCashReports = () => {
  const { showSuccess, showError } = useToast();
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('single');
  const [singleDate, setSingleDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (reportType === 'single') {
        params.date = singleDate;
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const response = await api.get('/api/petty-cash/reports', { params });
      setReports(response.data);
    } catch (err) {
      showError('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reports) return;
    try {
      // Expense Sheet
      const expenseData = reports.expenses?.map((e, i) => ({
        'SL': i + 1,
        'DATE': e.date,
        'RAV': e.rav,
        'AMOUNT': e.amount,
        'REMARKS': e.remarks || '',
        'STATUS': e.status
      })) || [];

      // Salary Sheet
      const salaryData = reports.salaries?.map((s, i) => ({
        'SL': i + 1,
        'DATE': s.date,
        'NAME': s.name,
        'ROLE': s.role,
        'AMOUNT': s.attendance === 'Absent' ? '—' : s.amount,
        'ATTENDANCE': s.attendance
      })) || [];

      const wb = XLSX.utils.book_new();
      const wsExpense = XLSX.utils.json_to_sheet(expenseData);
      const wsSalary = XLSX.utils.json_to_sheet(salaryData);

      XLSX.utils.book_append_sheet(wb, wsExpense, 'Expenses');
      XLSX.utils.book_append_sheet(wb, wsSalary, 'Salaries');

      XLSX.writeFile(wb, `Petty_Cash_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSuccess('Excel exported successfully');
    } catch (err) {
      showError('Failed to export Excel');
    }
  };

  const exportToPDF = () => {
    if (!reports) return;
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(16);
      doc.text('PETTY CASH MANAGEMENT', 105, 15, { align: 'center' });
      doc.setFontSize(12);
      doc.text('Comprehensive Report', 105, 25, { align: 'center' });
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);

      // Expenses Section
      doc.setFontSize(12);
      doc.text('EXPENSES SECTION', 14, 45);
      const expenseData = reports.expenses?.map((e, i) => [
        i + 1, e.date, e.rav, `${e.amount?.toLocaleString()}`, e.remarks || '', e.status
      ]) || [];
      
      autoTable(doc, {
        startY: 50,
        head: [['#', 'Date', 'Rav', 'Amount', 'Remarks', 'Status']],
        body: expenseData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9 }
      });

      // Salaries Section
      const salaryStartY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text('SALARY SECTION (Grouped by Role)', 14, salaryStartY);
      
      const salaryData = reports.salaries?.map((s, i) => [
        i + 1, s.date, s.name, s.role, 
        s.attendance === 'Absent' ? '—' : `${s.amount?.toLocaleString()}`, 
        s.attendance
      ]) || [];
      
      autoTable(doc, {
        startY: salaryStartY + 5,
        head: [['#', 'Date', 'Name', 'Role', 'Amount', 'Attendance']],
        body: salaryData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9 },
        columnStyles: {
          5: { 
            cellWidth: 25,
            halign: 'center'
          }
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 5) {
            if (data.cell.raw === 'Present') {
              data.cell.styles.textColor = [16, 185, 129];
              data.cell.styles.fontStyle = 'bold';
            } else if (data.cell.raw === 'Absent') {
              data.cell.styles.textColor = [239, 68, 68];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });

      // Summary Section
      const summaryY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('SUMMARY', 14, summaryY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      // Left column
      doc.text(`Expenses: ${reports.summary.total_expenses?.toLocaleString()}`, 14, summaryY + 7);
      doc.text(`Salary: ${reports.summary.total_salary?.toLocaleString()}`, 14, summaryY + 14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total: ${reports.summary.grand_total?.toLocaleString()}`, 14, summaryY + 21);
      
      // Right column
      doc.setFont('helvetica', 'normal');
      doc.text(`Present: ${reports.summary.total_present}`, 120, summaryY + 7);
      doc.text(`Absent: ${reports.summary.total_absent}`, 120, summaryY + 14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Strength: ${reports.summary.total_strength}`, 120, summaryY + 21);

      doc.save(`Petty_Cash_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      showSuccess('PDF exported successfully');
    } catch (err) {
      console.error('PDF Export Error:', err);
      showError('Failed to export PDF');
    }
  };

  return (
    <div className="space-y-6">
      {/* Report Filters */}
      <Card style={cardStyle}>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Report Type</Label>
            <RadioGroup value={reportType} onValueChange={setReportType} className="flex gap-6 mt-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single" />
                <Label htmlFor="single" className="cursor-pointer">Single Date</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="range" id="range" />
                <Label htmlFor="range" className="cursor-pointer">Date Range</Label>
              </div>
            </RadioGroup>
          </div>

          {reportType === 'single' ? (
            <div>
              <Label>Select Date</Label>
              <Input 
                type="date" 
                value={singleDate} 
                onChange={(e) => setSingleDate(e.target.value)} 
                className="w-40 mt-2"
                data-testid="single-date-input"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <Input 
                  type="date" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-2"
                  data-testid="date-from-input"
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input 
                  type="date" 
                  value={dateTo} 
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-2"
                  data-testid="date-to-input"
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button onClick={fetchReports} disabled={loading} data-testid="generate-report-btn">
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
            {reports && (
              <>
                <Button variant="outline" onClick={exportToExcel} data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button variant="outline" onClick={exportToPDF} data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Preview */}
      {reports && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Side Summary */}
            <Card style={cardStyle}>
              <CardHeader>
                <CardTitle className="text-base">Financial Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Expenses:</span>
                  <span className="font-bold text-red-600">{(reports.summary.total_expenses || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Salary:</span>
                  <span className="font-bold text-blue-600">{(reports.summary.total_salary || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-semibold text-lg">Total:</span>
                  <span className="font-bold text-lg text-gray-900">{(reports.summary.grand_total || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Right Side Summary */}
            <Card style={cardStyle}>
              <CardHeader>
                <CardTitle className="text-base">Attendance Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Present:</span>
                  <span className="font-bold text-green-600">{reports.summary.total_present || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Absent:</span>
                  <span className="font-bold text-red-600">{reports.summary.total_absent || 0}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-semibold text-lg">Strength:</span>
                  <span className="font-bold text-lg text-gray-900">{reports.summary.total_strength || 0}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Expense Section */}
          <Card style={cardStyle}>
            <CardHeader>
              <CardTitle>Expense Section</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {reports.expenses?.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Rav</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Remarks</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.expenses.map((e, i) => (
                          <TableRow key={e.id}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{e.date}</TableCell>
                            <TableCell className="font-medium">{e.rav}</TableCell>
                            <TableCell className="font-semibold">{e.amount?.toLocaleString()}</TableCell>
                            <TableCell className="text-gray-600">{e.remarks || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={e.status === 'Paid' ? 'success' : 'destructive'}>
                                {e.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t font-semibold text-gray-800">
                    Total Expenses: {(reports.summary.total_expenses || 0).toLocaleString()}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">No expenses found</div>
              )}
            </CardContent>
          </Card>

          {/* Salary Section */}
          <Card style={cardStyle}>
            <CardHeader>
              <CardTitle>Salary Section (Grouped by Role)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {reports.salaries?.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Attendance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.salaries.map((s, i) => (
                          <TableRow key={s.id}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{s.date}</TableCell>
                            <TableCell className="font-medium">{s.name}</TableCell>
                            <TableCell className="font-medium text-blue-700">{s.role}</TableCell>
                            <TableCell className="font-semibold">
                              {s.attendance === 'Absent' ? '—' : `${(s.amount || 0).toLocaleString()}`}
                            </TableCell>
                            <TableCell>
                              <Badge variant={s.attendance === 'Present' ? 'success' : 'destructive'}>
                                {s.attendance}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t font-semibold text-gray-800">
                    Total Salary: {(reports.summary.total_salary || 0).toLocaleString()}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">No salary entries found</div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const PettyCash = () => (
  <LayoutWrapper title="Petty Cash Management" subtitle="Manage daily expenses and salaries">
    <Routes>
      <Route index element={<PettyCashDashboard />} />
      <Route path="expenses" element={<ExpenseEntry />} />
      <Route path="salaries" element={<SalaryEntry />} />
      <Route path="reports" element={<PettyCashReports />} />
    </Routes>
  </LayoutWrapper>
);

export default PettyCash;
